import { IPublicClientApplication } from '@azure/msal-browser';
import { AiScrubScope } from '../../models';
import { ApiEndpointCallStatesStore } from '../api-endpoint-call-states-store';
import { FetchContext } from '../fetch-context';
import { promiseWithObservableState } from '../promise-with-observable-state';
import { withApiAccessToken } from '../with-api-access-token';
import {
  cancelAiScrubBatch,
  createAiScrubStreamToken,
  getAiAssessment,
  getAiScrubBatchList,
  getAiScrubBatchStatus,
  getAiScrubDiagnosticsExport,
  getCountryAiAssessment,
  getEvaluationComparison,
  getHumanRatings,
  getLatestAiScrubBatchRun,
  startAiScrubBatch,
} from './index';

export const createAiScrubbingApiEndpoints = (
  fetchContext: FetchContext,
  apiEndpointCallStates: ApiEndpointCallStatesStore,
) => ({
  startAiScrubBatch: (instance: IPublicClientApplication, awardId: number, rubricPrompt: string) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => startAiScrubBatch(fetchContext, awardId, rubricPrompt, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('startAiScrubBatch', isInFlight);
    },
  ),
  getAiScrubBatchStatus: (instance: IPublicClientApplication, awardId: number, batchRunId: string) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getAiScrubBatchStatus(fetchContext, awardId, batchRunId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getAiScrubBatchStatus', isInFlight);
    },
  ),
  createAiScrubStreamToken: (instance: IPublicClientApplication, awardId: number, batchRunId: string) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => createAiScrubStreamToken(fetchContext, awardId, batchRunId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('createAiScrubStreamToken', isInFlight);
    },
  ),
  getAiScrubDiagnosticsExport: (instance: IPublicClientApplication, awardId: number, batchRunId: string) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getAiScrubDiagnosticsExport(fetchContext, awardId, batchRunId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getAiScrubDiagnosticsExport', isInFlight);
    },
  ),
  getAiScrubBatchList: (instance: IPublicClientApplication, awardId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getAiScrubBatchList(fetchContext, awardId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getAiScrubBatchList', isInFlight);
    },
  ),
  cancelAiScrubBatch: (instance: IPublicClientApplication, awardId: number, batchRunId: string) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => cancelAiScrubBatch(fetchContext, awardId, batchRunId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('cancelAiScrubBatch', isInFlight);
    },
  ),
  cancelCountryAiScrubBatch: (instance: IPublicClientApplication, awardId: number, batchRunId: string) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => cancelAiScrubBatch(fetchContext, awardId, batchRunId, accessToken, 'country-awards'),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('cancelCountryAiScrubBatch', isInFlight);
    },
  ),
  startCountryAiScrubBatch: (instance: IPublicClientApplication, awardId: number, rubricPrompt: string, countryId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => startAiScrubBatch(fetchContext, awardId, rubricPrompt, accessToken, 'country-awards', countryId),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('startCountryAiScrubBatch', isInFlight);
    },
  ),
  getCountryAiScrubBatchStatus: (instance: IPublicClientApplication, awardId: number, batchRunId: string) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getAiScrubBatchStatus(fetchContext, awardId, batchRunId, accessToken, 'country-awards'),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getCountryAiScrubBatchStatus', isInFlight);
    },
  ),
  getLatestAiScrubBatchRun: (instance: IPublicClientApplication, awardId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getLatestAiScrubBatchRun(fetchContext, awardId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getLatestAiScrubBatchRun', isInFlight);
    },
  ),
  getLatestCountryAiScrubBatchRun: (instance: IPublicClientApplication, awardId: number, countryId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getLatestAiScrubBatchRun(fetchContext, awardId, accessToken, 'country-awards', countryId),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getLatestCountryAiScrubBatchRun', isInFlight);
    },
  ),
  createCountryAiScrubStreamToken: (instance: IPublicClientApplication, awardId: number, batchRunId: string) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => createAiScrubStreamToken(fetchContext, awardId, batchRunId, accessToken, 'country-awards'),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('createCountryAiScrubStreamToken', isInFlight);
    },
  ),
  getAiAssessment: (instance: IPublicClientApplication, awardId: number, nominationId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getAiAssessment(fetchContext, awardId, nominationId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getAiAssessment', isInFlight);
    },
  ),
  getCountryAiAssessment: (instance: IPublicClientApplication, programId: number, countryId: number, nominationId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getCountryAiAssessment(fetchContext, programId, countryId, nominationId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getCountryAiAssessment', isInFlight);
    },
  ),
  getEvaluationComparison: (instance: IPublicClientApplication, awardId: number, scope?: AiScrubScope, countryId?: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getEvaluationComparison(fetchContext, awardId, accessToken, scope, countryId),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getEvaluationComparison', isInFlight);
    },
  ),
  getHumanRatings: (instance: IPublicClientApplication, awardId: number, nominationId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getHumanRatings(fetchContext, awardId, nominationId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getHumanRatings', isInFlight);
    },
  ),
});
